import { CircularProgress, InteractableComponent } from '@shared/components';
import {
  svgBlank,
  svgEML,
  svgGIF,
  svgJPG,
  svgPDF,
  svgPNG,
  svgTXT,
  svgXLS,
} from '@shared/components/attachment/svgs';
import { InteractableComponentProps } from '@shared/components/interactable-component/InteractableComponent';
import { Document } from '@shared/types/document';
import { StyleProps } from '@shared/utils';
import clsx from 'clsx';
import { ReactElement, useEffect, useState } from 'react';
import CsvViewer from '../csv-viewer/CsvViewer';
import { el, ROOT } from './classNames';
import { DocViewerMessage } from './DocViewerMessage';

type EmptyDocViewerProps = StyleProps & {
  message?: string;
};
export function EmptyDocViewerComponent(
  props: EmptyDocViewerProps
): React.ReactElement {
  return (
    <div className={clsx(ROOT, props.className)}>
      <DocViewerMessage message={props.message || 'No document to display'} />
    </div>
  );
}

const getExtension = (url: string) => {
  const filename = url.split('?')[0];
  const parts = filename.split('.');
  return parts[parts.length - 1].toLowerCase();
};

const hasFileExtension = (fileName: string) => {
  const lastPathSegment = fileName.split('/').pop() ?? '';
  const lastDotIndex = lastPathSegment.lastIndexOf('.');
  return lastDotIndex > 0 && lastDotIndex < lastPathSegment.length - 1;
};

export function getDocumentExtension(url: string, fileName?: string): string {
  return getExtension(fileName && hasFileExtension(fileName) ? fileName : url);
}

type InteractiveProps = Omit<InteractableComponentProps, 'asChild'>;
const IconButton = (props: InteractiveProps): ReactElement => (
  <InteractableComponent {...props} asChild>
    {props.children}
  </InteractableComponent>
);

const getIconForType = (type: string) => {
  switch (type) {
    case 'html':
      return svgEML;
    case 'pdf':
      return svgPDF;
    case 'png':
      return svgPNG;
    case 'jpg':
    case 'jpeg':
      return svgJPG;
    case 'gif':
      return svgGIF;
    case 'txt':
      return svgTXT;
    case 'xlsx':
    case 'xls':
    case 'xlsm':
      return svgXLS;
    default:
      return svgBlank;
  }
};

const scoreExtension = (ext: string) => {
  switch (ext) {
    case 'html':
      return 0;
    case 'pdf':
      return 1;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
      return 2;
    case 'txt':
      return 3;
    default:
      return 4;
  }
};

// TODO(mike): This avoids refreshing the document every time we poll
// on an order. We should ideally not be getting a new signed url so often.
function hasExpired() {
  return false;
}

function isSameDoc(url1: string, url2: string) {
  const filename1 = url1.split('?')[0];
  const filename2 = url2.split('?')[0];
  return filename1 === filename2;
}

function MicrosoftDocumentViewer(props: { src: string; title: string }) {
  const encodedUrl = encodeURIComponent(props.src);
  const embedURL = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;

  return <iframe className={el`frame`} src={embedURL} title={props.title} />;
}

export function DocRenderer(props: {
  url: string;
  fileName?: string;
}): ReactElement {
  const [url, setUrl] = useState(props.url);

  useEffect(() => {
    if (!isSameDoc(url, props.url) || hasExpired()) {
      setUrl(props.url);
    }
  }, [props.url]);
  const ext = getDocumentExtension(url, props.fileName);
  const args = '';

  switch (ext) {
    case 'html':
    case 'txt':
      return (
        <iframe
          className={el`frame`}
          src={`${url}${args}`}
          title="Document Viewer"
          // NOTE(max): Disables JavaScript execution in the iframe.
          // for XSS protection.
          sandbox="allow-top-navigation-by-user-activation"
        />
      );
    case 'pdf':
      return (
        <iframe
          className={el`frame`}
          src={`${url}${args}`}
          title="Document Viewer"
        />
      );
    case 'xlsx':
    case 'xls':
    case 'docx':
      return <MicrosoftDocumentViewer src={url} title="Document Viewer" />;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
      return (
        <div className={el`img-container`}>
          <img
            className={el`img`}
            src={`${url}${args}`}
            alt="Document Viewer"
          />
        </div>
      );
    case 'csv':
      return (
        <CsvViewer src={url} title="CSV Viewer" fileName={props.fileName} />
      );

    default:
      return <DocViewerMessage message={`Cannot display ${ext} files`} />;
  }
}

type DocViewerProps = StyleProps & {
  isLoading: boolean;
  documents: Document[];
};
export function DocViewer({
  isLoading,
  documents,
  ...props
}: DocViewerProps): React.ReactElement {
  const [docIdx, setDocIdx] = useState(0);

  if (isLoading) {
    return (
      <div className={clsx(ROOT, props.className)}>
        <div className={el`doc-container`}>
          <div className={el`doc`}>
            <CircularProgress />
          </div>
        </div>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <EmptyDocViewerComponent
        {...props}
        message={'No documents to display...'}
      />
    );
  }

  const sortedDocs = [...documents].sort((a, b) => {
    const aExt = getExtension(a.url);
    const bExt = getExtension(b.url);
    return scoreExtension(aExt) - scoreExtension(bExt);
  });

  const docTypes = sortedDocs.map((doc) => getExtension(doc.url)) || [];
  const safeIdx = Math.min(docIdx, docTypes.length - 1);
  const doc = sortedDocs[safeIdx];

  return (
    <div className={clsx(ROOT, props.className)}>
      <div className={el`doc-container`}>
        <DocRenderer url={doc.url} />
        {docTypes.length > 1 && (
          <div className={el`doc-selector`}>
            {docTypes.map((type, idx) => (
              <IconButton
                className={el`doc-icon`}
                key={idx}
                onPress={() => setDocIdx(idx)}
                isPressed={idx === docIdx}
              >
                {getIconForType(type)}
              </IconButton>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
